/**
 * A deliberately tiny Markdown renderer for the legal documents.
 *
 * ─── Why not `innerHTML`, ever ──────────────────────────────────────────────
 * `LegalPage.content` is an untyped `String` column that any admin can edit
 * from `/admin`, and core neither sanitises nor parses it. Assigning it to
 * `innerHTML` — or assigning HTML DERIVED from it, which is the same thing —
 * is stored XSS in exchange for bold text.
 *
 * So this never produces a string of HTML. It produces DOM nodes, each built
 * with `document.createElement` and filled with `textContent`. There is no
 * code path by which a `<` in the source becomes a tag, because no code path
 * parses HTML at all. `markdown-lite.test.ts` holds that line with a document
 * full of script tags and event handlers, and asserts the result contains no
 * element the grammar below can produce.
 *
 * ─── Why render at all ──────────────────────────────────────────────────────
 * `legal-pages.ts` chose Markdown precisely so a client could get away with
 * dumping it into a `pre-wrap` block, and that is what shipped first. It is
 * safe and it is legible, and it also shows the reader literal `##` and `**`
 * on a page that is meant to look like a published policy.
 *
 * ─── The whole grammar ──────────────────────────────────────────────────────
 * Blocks are separated by blank lines. Anything not matching a rule below is a
 * paragraph, with its source text intact — an unsupported construct degrades
 * to visible plain text rather than disappearing.
 *
 *   `# ` / `## ` / `### `   headings (h2/h3/h4 — the page owns the h1)
 *   `- ` / `* `             unordered list; every line in the block must match
 *   `1. `                   ordered list; same rule
 *   `> `                    blockquote
 *   `---`                   horizontal rule
 *   `**bold**`              inline, inside any of the above
 *   `_italic_`              inline, same
 *
 * Deliberately NOT supported: links, images, inline code, tables, HTML. A
 * legal document needs none of them, and a link is the one construct where a
 * URL from the database would end up in an attribute.
 */

/** Parses `text` into nodes and appends them to `host`. `host` is cleared first. */
export function renderMarkdown(host: HTMLElement, text: string): void {
  host.textContent = '';
  for (const block of splitBlocks(text)) {
    const el = renderBlock(block);
    if (el) host.appendChild(el);
  }
}

function splitBlocks(text: string): string[][] {
  const blocks: string[][] = [];
  let current: string[] = [];
  // \r\n as well as \n: the column is admin-editable through a browser form,
  // and a textarea submits CRLF.
  for (const raw of text.replace(/\r\n?/g, '\n').split('\n')) {
    if (raw.trim() === '') {
      if (current.length) blocks.push(current);
      current = [];
      continue;
    }
    current.push(raw);
  }
  if (current.length) blocks.push(current);
  return blocks;
}

const HEADING_TAG: Record<number, string> = { 1: 'h2', 2: 'h3', 3: 'h4' };

function renderBlock(lines: string[]): HTMLElement | null {
  const first = lines[0]!;

  const heading = /^(#{1,3})\s+(.*)$/.exec(first);
  if (heading) {
    const el = document.createElement(HEADING_TAG[heading[1]!.length]!);
    appendInline(el, heading[2]!);
    return el;
  }

  if (/^-{3,}$/.test(first.trim()) && lines.length === 1) return document.createElement('hr');

  // A list if the block STARTS with a marker. Lines after it that carry no
  // marker are a continuation of the item above — CommonMark's "lazy
  // continuation", and the behaviour that matters here in practice: these
  // documents are hand-edited in a textarea, so a long bullet gets soft-
  // wrapped and its second line has no dash. Requiring every line to carry a
  // marker (the first version) turned a whole wrapped list into one run-on
  // paragraph with the dashes still in it, which is exactly what the news
  // board shipped with until this was fixed.
  const bullet = /^\s*[-*]\s+/;
  const numbered = /^\s*\d+\.\s+/;
  if (bullet.test(first)) return listOf('ul', lines, bullet);
  if (numbered.test(first)) return listOf('ol', lines, numbered);

  if (lines.every((l) => /^\s*>\s?/.test(l))) {
    const quote = document.createElement('blockquote');
    appendInline(quote, lines.map((l) => l.replace(/^\s*>\s?/, '')).join(' '));
    return quote;
  }

  const p = document.createElement('p');
  // Soft-wrapped source lines are one paragraph, the same as every Markdown
  // implementation.
  appendInline(p, lines.join(' '));
  return p;
}

/**
 * Splits one line into text and emphasis, appending both as nodes.
 *
 * The regex finds the MARKERS; everything between them is added with
 * `textContent`, so nothing in the source is ever interpreted as markup.
 */
/**
 * Builds a list, folding unmarked lines into the item above them.
 *
 * A marker line opens a new `<li>`; anything else is appended to the item in
 * progress with a space, the same way a soft-wrapped paragraph is joined.
 */
function listOf(tag: 'ul' | 'ol', lines: string[], marker: RegExp): HTMLElement {
  const list = document.createElement(tag);
  let item: HTMLElement | null = null;
  for (const line of lines) {
    if (marker.test(line)) {
      item = document.createElement('li');
      appendInline(item, line.replace(marker, ''));
      list.appendChild(item);
      continue;
    }
    // Always open by now: the caller only reaches here when the FIRST line
    // carried a marker, so an item exists before any unmarked line arrives.
    if (item) appendInline(item, ` ${line.trim()}`);
  }
  return list;
}

function appendInline(host: HTMLElement, line: string): void {
  const pattern = /\*\*([^*]+)\*\*|_([^_]+)_/g;
  let cursor = 0;
  let m: RegExpExecArray | null;
  while ((m = pattern.exec(line)) !== null) {
    if (m.index > cursor) host.appendChild(document.createTextNode(line.slice(cursor, m.index)));
    const el = document.createElement(m[1] !== undefined ? 'strong' : 'em');
    el.textContent = m[1] ?? m[2] ?? '';
    host.appendChild(el);
    cursor = m.index + m[0].length;
  }
  if (cursor < line.length) host.appendChild(document.createTextNode(line.slice(cursor)));
}
